'use client';

import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';

export type ExistingReview = {
  id: number;
  rating: number;
  body: string;
};

const MIN_LEN = 100;
const MAX_LEN = 2000;

export default function WriteReviewModal({
  open,
  onClose,
  subjectPersonId,
  subjectName,
  existing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  subjectPersonId: number;
  subjectName: string;
  existing?: ExistingReview | null;
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const [rating, setRating] = useState<number>(existing?.rating ?? 0);
  const [hover, setHover] = useState<number>(0);
  const [body, setBody] = useState<string>(existing?.body ?? '');
  const [busy, setBusy] = useState(false);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setRating(existing?.rating ?? 0);
    setBody(existing?.body ?? '');
    const t = setTimeout(() => { try { textRef.current?.focus(); } catch {} }, 30);
    return () => clearTimeout(t);
  }, [open, existing]);

  const trimmed = body.trim();
  const canSubmit = rating >= 1 && rating <= 5 && trimmed.length >= MIN_LEN && trimmed.length <= MAX_LEN && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await fetch('/api/proxy/api/reviews', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subjectPersonId, rating, body: trimmed }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'unknown' }));
        const reason = typeof err?.error === 'string' ? err.error : 'submit_failed';
        toast({ title: 'Could not save review', description: reason, variant: 'destructive' });
        return;
      }
      const saved = await res.json();
      if (saved?.status === 'pending') {
        toast({ title: 'Review submitted for review', description: 'It will appear once approved.' });
      } else {
        toast({ title: existing ? 'Review updated' : 'Review posted' });
      }
      onSaved?.();
      onClose();
    } catch (e: any) {
      toast({ title: 'Could not save review', description: e?.message || '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit your review' : `Write a review of ${subjectName}`}</DialogTitle>
        </DialogHeader>

        <div className="mt-1 flex items-center gap-1" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || rating) >= n;
            return (
              <button
                key={n}
                type="button"
                aria-label={`${n} star${n === 1 ? '' : 's'}`}
                onMouseEnter={() => setHover(n)}
                onClick={() => setRating(n)}
                className="p-1 -m-1 focus:outline-none focus:ring-2 focus:ring-ring rounded"
              >
                <Star
                  className={'h-6 w-6 ' + (active ? 'fill-current' : 'text-muted-foreground')}
                  strokeWidth={1.75}
                />
              </button>
            );
          })}
          {rating > 0 ? (
            <span className="ml-2 text-sm text-muted-foreground tabular-nums">{rating}/5</span>
          ) : null}
        </div>

        <textarea
          ref={textRef}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_LEN))}
          rows={5}
          placeholder="Describe your experience. Minimum 100 characters."
          aria-label="Review body"
          className="mt-3 w-full border rounded p-2 bg-background text-foreground"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>
            {trimmed.length < MIN_LEN
              ? `${MIN_LEN - trimmed.length} more to go`
              : `${trimmed.length} / ${MAX_LEN}`}
          </span>
          <span>Reviews violating community guidelines may be hidden.</span>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {busy ? 'Saving…' : existing ? 'Save changes' : 'Post review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
